import React, { useState } from "react";
import { Modal, Platform, Pressable, StyleSheet, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import styles from "../NotificationsCommsScreen.style";
import { NotificationType, PortalNotification } from "../hooks/useNotificationsComms";

const MESSAGE_PREVIEW_LENGTH = 200;

type Props = {
  notification: PortalNotification;
  onToggleRead: (id: string) => void;
  onDelete?: (id: string) => void;
  onPressLinkedTicket?: (ticketId: string) => void;
  onEdit?: (id: string) => void;
  isDark?: boolean;
  colors?: any;
};

// ── Badge config by notification type ────────────────────────────────────────

const TYPE_LABEL: Record<NotificationType, string> = {
  incident: "Incident",
  maint: "Maintenance",
  general: "General",
};

const getTypeBadgeStyles = (type: NotificationType) => {
  switch (type) {
    case "incident":
      return {
        badge: styles.badgeIncident,
        text: styles.badgeIncidentText,
        dot: styles.badgeIncidentDot,
      };
    case "maint":
      return {
        badge: styles.badgeMaint,
        text: styles.badgeMaintText,
        dot: styles.badgeMaintDot,
      };
    default:
      return {
        badge: styles.badgeGeneral,
        text: styles.badgeGeneralText,
        dot: styles.badgeGeneralDot,
      };
  }
};

const getRowBorderStyle = (type: NotificationType) => {
  switch (type) {
    case "incident":
      return styles.notifRowIncident;
    case "maint":
      return styles.notifRowMaint;
    default:
      return styles.notifRowGeneral;
  }
};

// ─────────────────────────────────────────────────────────────────────────────

const NotificationItem: React.FC<Props> = ({
  notification,
  onToggleRead,
  onDelete,
  onPressLinkedTicket,
  onEdit,
  isDark,
  colors,
}) => {
  const [rowHovered, setRowHovered] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [expandedMsg, setExpandedMsg] = useState(false);

  const isUnread = notification.readStatus === "unread";
  const typeBadge = getTypeBadgeStyles(notification.type);
  const borderStyle = getRowBorderStyle(notification.type);

  // Truncate message to preview length
  const needsTruncation = notification.message.length > MESSAGE_PREVIEW_LENGTH;
  const displayMsg =
    needsTruncation && !expandedMsg ? notification.message.slice(0, MESSAGE_PREVIEW_LENGTH) + "…" : notification.message;

  // Dark mode badge colors for type badges
  const getDarkTypeBadge = (type: NotificationType) => {
    switch (type) {
      case "incident": return { bg: "#3D0A00", text: "#FF8A80", dot: "#FF5252" };
      case "maint":    return { bg: "#0d2a44", text: "#82b4ff", dot: "#3D8BFF" };
      default:         return { bg: "#2B2B2B", text: "#B0B0B0", dot: "#6E6E6E" };
    }
  };
  const darkTypeBadge = isDark ? getDarkTypeBadge(notification.type) : null;

  return (
    <Pressable
      onHoverIn={() => setRowHovered(true)}
      onHoverOut={() => setRowHovered(false)}
      style={[
        styles.notifRow,
        isDark && { backgroundColor: "#202020", borderColor: "#404040" },
        borderStyle,
        isUnread && !isDark ? styles.notifRowUnread : null,
        isUnread && isDark ? { backgroundColor: "#252525" } : null,
        rowHovered ? { opacity: 0.97 } : null,
      ]}
      onPress={() => {
        if (isUnread) onToggleRead(notification.id);
      }}
    >
      <View style={styles.notifInner}>
        {/* Top row: unread dot + title + actions */}
        <View style={styles.notifTop}>
          <View style={{ flexDirection: "row", flex: 1, minWidth: 0 }}>
            {isUnread ? <View style={[styles.unreadDot, isDark && { backgroundColor: "#3D8BFF" }]} /> : null}
            <View style={styles.notifTitleBlock}>
              <Text
                style={[styles.notifTitle, isDark && { color: "#F2F2F2" }, isUnread ? styles.notifTitleUnread : null]}
                numberOfLines={2}
              >
                {notification.title}
              </Text>
              {/* Badge row */}
              <View style={styles.notifBadgeRow}>
                {/* Type badge */}
                <View style={[styles.badge, darkTypeBadge ? { backgroundColor: darkTypeBadge.bg } : typeBadge.badge]}>
                  <View style={[styles.badgeDot, darkTypeBadge ? { backgroundColor: darkTypeBadge.dot } : typeBadge.dot]} />
                  <Text style={[styles.badgeText, darkTypeBadge ? { color: darkTypeBadge.text } : typeBadge.text]}>
                    {TYPE_LABEL[notification.type]}
                  </Text>
                </View>
                {/* Active status badge */}
                {notification.statusTab === "active" ? (
                  <View style={[styles.badge, isDark ? { backgroundColor: "#14301a" } : styles.badgeActive]}>
                    <View style={[styles.badgeDot, isDark ? { backgroundColor: "#4ade80" } : styles.badgeActiveDot]} />
                    <Text style={[styles.badgeText, isDark ? { color: "#4ade80" } : styles.badgeActiveText]}>Active</Text>
                  </View>
                ) : null}
                {/* Homepage Banner badge */}
                {notification.isHomepageBanner ? (
                  <View style={[styles.badge, isDark ? { backgroundColor: "#0d2a44" } : styles.badgeHomepage]}>
                    <Text style={[styles.badgeText, isDark ? { color: "#82b4ff" } : styles.badgeHomepageText]}>
                      Homepage Banner
                    </Text>
                  </View>
                ) : null}
              </View>
            </View>
          </View>

          {/* Action buttons */}
          <View style={styles.notifActions}>
            {onEdit && (
              <Pressable
                style={({ pressed }) => [
                  styles.btnSecondary,
                  isDark && { backgroundColor: "#202020", borderColor: "#404040" },
                  { paddingVertical: 6, paddingHorizontal: 12 },
                  pressed ? styles.btnSecondaryPressed : null,
                ]}
                onPress={() => onEdit(notification.id)}
              >
                <Text style={[styles.btnSecondaryText, isDark && { color: "#3D8BFF" }, { fontSize: 12 }]}>Edit</Text>
              </Pressable>
            )}
            {onDelete && (
              <Pressable
                style={({ pressed }) => [styles.btnDangerSm, pressed ? styles.btnDangerSmPressed : null]}
                onPress={() => setShowDeleteConfirm(true)}
              >
                <MaterialIcons name="delete-outline" size={15} color="#C12C01" />
                <Text style={styles.btnDangerSmText}>Delete</Text>
              </Pressable>
            )}
          </View>
        </View>

        {/* Delete confirmation modal */}
        <Modal
          visible={showDeleteConfirm}
          transparent
          animationType="fade"
          onRequestClose={() => setShowDeleteConfirm(false)}
        >
          <Pressable
            style={confirmStyles.overlay}
            onPress={() => setShowDeleteConfirm(false)}
          />
          <View style={confirmStyles.wrap} pointerEvents="box-none">
            <View style={[confirmStyles.card, isDark && { backgroundColor: "#202020" }]}>
              <MaterialIcons name="delete-outline" size={28} color="#C12C01" style={{ marginBottom: 8 }} />
              <Text style={[confirmStyles.title, isDark && { color: "#F2F2F2" }]}>Delete notification?</Text>
              <Text style={[confirmStyles.body, isDark && { color: "#B0B0B0" }]}>
                "{notification.title}" will be permanently removed.
              </Text>
              <View style={confirmStyles.actions}>
                <Pressable
                  onPress={() => setShowDeleteConfirm(false)}
                  style={({ pressed }) => [confirmStyles.btnCancel, isDark && { backgroundColor: "#2B2B2B", borderColor: "#404040" }, pressed ? { opacity: 0.7 } : null]}
                >
                  <Text style={[confirmStyles.btnCancelText, isDark && { color: "#F2F2F2" }]}>Cancel</Text>
                </Pressable>
                <Pressable
                  onPress={() => { setShowDeleteConfirm(false); onDelete?.(notification.id); }}
                  style={({ pressed }) => [confirmStyles.btnDelete, pressed ? { opacity: 0.8 } : null]}
                >
                  <Text style={confirmStyles.btnDeleteText}>Delete</Text>
                </Pressable>
              </View>
            </View>
          </View>
        </Modal>

        {/* Message body with Read More / Show Less */}
        <Text style={[styles.notifMsg, isDark && { color: "#B0B0B0" }]}>
          {displayMsg}
          {needsTruncation && !expandedMsg ? (
            <Text
              style={styles.notifReadMoreText}
              onPress={() => setExpandedMsg(true)}
            >
              {" Read More"}
            </Text>
          ) : null}
          {needsTruncation && expandedMsg ? (
            <Text
              style={styles.notifReadMoreText}
              onPress={() => setExpandedMsg(false)}
            >
              {" Show Less"}
            </Text>
          ) : null}
        </Text>

        {/* Footer */}
        <View style={[styles.notifFooter, isDark && { borderTopColor: "#363636" }]}>
          <View style={styles.notifMeta}>
            {/* Location */}
            <View style={styles.notifMetaItem}>
              <MaterialIcons name="place" size={12} color={isDark ? "#6E6E6E" : "#818181"} />
              <Text style={[styles.notifMetaText, isDark && { color: "#B0B0B0" }]}>{notification.locations}</Text>
            </View>
            {/* Timestamp */}
            <View style={styles.notifMetaItem}>
              <MaterialIcons name="schedule" size={12} color={isDark ? "#6E6E6E" : "#818181"} />
              <Text style={[styles.notifMetaText, isDark && { color: "#B0B0B0" }]}>{notification.startedAt}</Text>
            </View>
            {/* Linked ticket */}
            {notification.linkedTicket ? (
              <View style={styles.notifMetaItem}>
                <Text style={[styles.notifMetaText, isDark && { color: "#B0B0B0" }]}>Linked to </Text>
                <Pressable onPress={() => onPressLinkedTicket?.(notification.linkedTicket!)}>
                  <Text style={[styles.notifMetaLink, isDark && { color: "#3D8BFF" }]}>{notification.linkedTicket}</Text>
                </Pressable>
              </View>
            ) : null}
          </View>

          {/* Stats */}
          <View style={styles.notifStats}>
            {notification.stats.notified !== undefined ? (
              <View style={styles.notifStat}>
                <Text style={[styles.notifStatNum, isDark && { color: "#F2F2F2" }]}>
                  {(notification.stats.recipients ?? notification.stats.notified).toLocaleString()}
                </Text>
                <Text style={[styles.notifStatLabel, isDark && { color: "#B0B0B0" }]}>
                  {notification.stats.recipients ? "recipients" : "notified"}
                </Text>
              </View>
            ) : null}
            {notification.stats.attached !== undefined ? (
              <View style={styles.notifStat}>
                <Text style={[styles.notifStatNum, isDark && { color: "#F2F2F2" }]}>{notification.stats.attached}</Text>
                <Text style={[styles.notifStatLabel, isDark && { color: "#B0B0B0" }]}>attached</Text>
              </View>
            ) : null}
          </View>
        </View>
      </View>
    </Pressable>
  );
};

export default NotificationItem;

const confirmStyles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0,0,0,0.45)",
  },
  wrap: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: "center",
    alignItems: "center",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 28,
    width: 320,
    maxWidth: "90%",
    alignItems: "center",
    ...(Platform.OS === "web"
      ? { boxShadow: "0 8px 32px rgba(0,0,0,0.18)" }
      : {
          shadowColor: "#000",
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.18,
          shadowRadius: 12,
          elevation: 10,
        }),
  },
  title: {
    fontSize: 16,
    fontWeight: "700",
    color: "#1a1a1a",
    marginBottom: 8,
    textAlign: "center",
    ...(Platform.OS === "web" ? { fontFamily: "Inter, sans-serif" } : {}),
  },
  body: {
    fontSize: 13,
    color: "#585858",
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 20,
    ...(Platform.OS === "web" ? { fontFamily: "Inter, sans-serif" } : {}),
  },
  actions: {
    flexDirection: "row",
    gap: 10,
    width: "100%",
    justifyContent: "center",
  },
  btnCancel: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 9,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#D0D0D0",
    backgroundColor: "#fff",
  },
  btnCancelText: {
    fontSize: 13,
    color: "#4b4b4b",
    fontWeight: "500",
    ...(Platform.OS === "web" ? { fontFamily: "Inter, sans-serif" } : {}),
  },
  btnDelete: {
    flex: 1,
    alignItems: "center",
    paddingVertical: 9,
    borderRadius: 8,
    backgroundColor: "#C12C01",
  },
  btnDeleteText: {
    fontSize: 13,
    color: "#fff",
    fontWeight: "600",
    ...(Platform.OS === "web" ? { fontFamily: "Inter, sans-serif" } : {}),
  },
});
